import {Avatar, Input, Radio, Spin} from 'antd'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {lookup_lookupInfoByValue_Api} from '../../apis'
import useAsyncAction from '../../modules/asyncCache/useAsyncAction'
import DrawDialog from '../drawer/dialog/DrawDialog'
import {IdentifierTypes} from '../../views/AppLocals/appFieldTypes'
import getAvatar from '../../helpers/getAvatar'
import {BsPersonFill} from 'react-icons/bs'
import getTitle from '../../helpers/getTitle'
import getAliasName from '../../helpers/getAliasName'
import {Link} from 'react-router-dom'
import getLinkToDetail from '../../helpers/getLinkToDetail'
import {HiCheckCircle, HiOutlineIdentification, HiOutlineMail, HiXCircle} from 'react-icons/hi'
import {MdOutlineManageSearch} from 'react-icons/md'
import {IoShieldCheckmarkOutline} from 'react-icons/io5'
import classNames from 'classnames'
import {churchTypeOptions} from "../../views/AppLocals/Church/consts/churchTypeOptions";
import {ChipGroup} from "../form/ModernForm";

const LOOKUP_DEBOUNCE = 700
// Số lần thử lại tối đa khi dispatch bị chặn bởi inFlightRef của useAsyncAction (request TRƯỚC
// chưa settle xong) - xem giải thích ở dispatchLookup().
const MAX_DISPATCH_RETRY = 10
const DISPATCH_RETRY_DELAY = 200

const SEARCH_MODES = {
    email: 'email',
    identifier: 'identifier-type',
}

const ModeTab = ({active, icon: Icon, label, onClick}) => (
    <button
        type="button"
        onClick={onClick}
        className={classNames(
            'flex-1 py-2.5 flex flex-center gap-1.5 rounded-lg text-sm font-semibold border transition-all cursor-pointer',
            active
                ? 'bg-primary border-primary text-white'
                : 'bg-white border-color-100 text-color-300 hover:border-primary hover:text-primary'
        )}>
        <Icon size={15}/>
        {label}
    </button>
)

const ResultCard = memo(({userInfo, isLoading, searchMode, onChoose, t}) => {
    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Spin size="default"/>
            </div>
        )
    }
    if (userInfo === null) {
        return (
            <div className="flex flex-col items-center gap-2 py-8 text-color-300">
                <MdOutlineManageSearch size={40} className="opacity-40"/>
                <span className="text-sm italic">{t('enter value to search')}</span>
            </div>
        )
    }
    if (!userInfo?.name) {
        return (
            <div className="flex flex-col gap-1.5 p-3 border border-orange-200 bg-orange-50 rounded-xl">
                <span className="flex items-center gap-2 text-orange-600">
                    <HiXCircle size={16} className="flex-shrink-0"/>
                    <span className="text-sm font-semibold">
                        {searchMode === SEARCH_MODES.email
                            ? t('not found email description')
                            : t('not found id code description')}
                    </span>
                </span>
                <span className="text-xs text-color-400 italic leading-snug pl-6">{t('not found suggestion')}</span>
            </div>
        )
    }
    const {owner, email} = userInfo
    const patronSaint = getAliasName(owner, 'patron_saint')
    return (
        <div className="flex flex-col gap-2">
            <span className="flex items-center gap-1.5 text-xs font-semibold text-green-600">
                <HiCheckCircle size={14} className="flex-shrink-0"/>
                {searchMode === SEARCH_MODES.email
                    ? t('this email address is associated with the account')
                    : t('this identifier is linked to the account')}
            </span>
            <div className="p-3 flex items-center gap-3 border border-green-400 bg-green-50 rounded-xl">
                <Avatar
                    size={44}
                    src={getAvatar(owner)}
                    icon={<BsPersonFill size={20} className="text-color-500"/>}
                    className="flex-shrink-0 flex flex-center background-200"
                />
                <div className="flex flex-col flex-1 min-w-0">
                    <Link
                        to={getLinkToDetail(owner)}
                        target="_blank"
                        className="font-bold text-sm text-color-000 no-underline truncate">
                        {!!patronSaint && (
                            <span className="mr-1 font-medium text-xs text-gray-600 capitalize">
                                {patronSaint}
                            </span>
                        )}
                        {getTitle(owner)}
                    </Link>
                    <span className="text-xs text-color-400 italic leading-tight tracking-wider truncate">{email}</span>
                </div>
                <button
                    type="button"
                    onClick={onChoose}
                    className="flex-shrink-0 px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-primary text-white hover:opacity-85 cursor-pointer border-0">
                    {t('choose')}
                </button>
            </div>
        </div>
    )
})

// Tách nội dung form ra component riêng + memo
// → Khi Toggle/drawer state thay đổi, chỉ DrawDialog re-render, không kéo theo UserLookupContent
const UserLookupContent = memo(({handleApply, toggle}) => {
    const t = useTranslate()

    const [searchMode, setSearchMode] = useState(SEARCH_MODES.email)
    const [identifierType, setIdentifierType] = useState(
        () => Object.values(IdentifierTypes)?.[0]?.value ?? 'identifier-type'
    )
    const [inputValue, setInputValue] = useState('')
    const [userInfo, setUserInfo] = useState(null)
    const [isSearching, setIsSearching] = useState(false)

    const debounceRef = useRef(null)
    const retryRef = useRef(null)
    const searchModeRef = useRef(searchMode)
    const identifierTypeRef = useRef(identifierType)
    const inputValueRef = useRef(inputValue)

    const {response, success, handleAsyncAction: lookupInfo} = useAsyncAction({
        apiInfo: lookup_lookupInfoByValue_Api,
    })

    // Dùng ref thay vì sync qua useEffect — tránh thêm render cycle
    const lookupRef = useRef(lookupInfo)
    lookupRef.current = lookupInfo  // luôn fresh, không cần useEffect
    searchModeRef.current = searchMode
    identifierTypeRef.current = identifierType
    inputValueRef.current = inputValue

    // searchTokenRef: đánh dấu lượt tìm kiếm MỚI NHẤT - dùng để 1 retry cũ (xem dispatchLookup) tự
    // biết mình đã lỗi thời và dừng lại, không đè lên lượt tìm kiếm mới hơn.
    const searchTokenRef = useRef(0)
    // pendingSearchedValueRef: giá trị THỰC SỰ đã gửi lên server cho request đang chờ - so với
    // inputValueRef lúc nhận kết quả để chắc chắn không hiển thị nhầm kết quả của 1 giá trị cũ.
    const pendingSearchedValueRef = useRef(null)

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
            if (retryRef.current) clearTimeout(retryRef.current)
        }
    }, [])

    useEffect(() => {
        if (pendingSearchedValueRef.current === null) return
        // Chỉ áp kết quả (và chỉ tắt "đang tìm") nếu nó đúng là kết quả của giá trị đang HIỂN THỊ
        // hiện tại - nếu người dùng đã gõ tiếp sang giá trị khác trong lúc chờ, đây là response của
        // 1 lượt tìm CŨ đã lỗi thời, bỏ qua hoàn toàn (kể cả không tắt spinner), giữ nguyên trạng
        // thái "đang tìm" cho tới khi đúng response của lượt tìm MỚI NHẤT trả về.
        if (pendingSearchedValueRef.current !== inputValueRef.current?.trim()) return
        setIsSearching(false)
        if (success) {
            setUserInfo(_.get(response, 'data', {}))
        } else {
            setUserInfo({})
        }
    }, [success, response])

    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

    // Dispatch thật sự lên useAsyncAction - có thể bị CHẶN (trả về undefined) bởi inFlightRef của
    // hook nếu request TRƯỚC (của lượt gõ nhanh hơn) chưa settle xong. Không retry thì request này
    // bị "nuốt" mất hoàn toàn - đã gặp thực tế: gõ nhanh tới ký tự cuối, request cho giá trị đó bị
    // chặn, cuối cùng chỉ có kết quả (not-found) của giá trị gõ THIẾU trước đó hiện ra, trông như
    // "mất ký tự cuối". Thử lại sau khoảng ngắn, tối đa MAX_DISPATCH_RETRY lần rồi thôi (không phải
    // lỗi mạng, không cần báo lỗi - hiếm khi chạm ngưỡng này).
    const dispatchLookup = useCallback((value, mode, idType, token, attempt = 0) => {
        if (searchTokenRef.current !== token) return
        const action = lookupRef.current({}, {
            search_type: 'user',
            input_type: mode === SEARCH_MODES.email ? 'email' : 'identifier_code',
            input_value: value,
            ...(mode !== SEARCH_MODES.email ? {identifier_type: idType} : {}),
        })
        if (!action) {
            if (attempt < MAX_DISPATCH_RETRY) {
                retryRef.current = setTimeout(
                    () => dispatchLookup(value, mode, idType, token, attempt + 1),
                    DISPATCH_RETRY_DELAY
                )
            }
            return
        }
        pendingSearchedValueRef.current = value
    }, [])

    const triggerLookup = useCallback((value, mode, idType) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
            debounceRef.current = null
        }
        if (retryRef.current) {
            clearTimeout(retryRef.current)
            retryRef.current = null
        }

        const trimmedValue = value?.trim() ?? ''

        if (!trimmedValue) {
            setUserInfo(null)
            setIsSearching(false)
            return
        }

        if (mode === SEARCH_MODES.email && !EMAIL_REGEX.test(trimmedValue)) {
            setUserInfo(null)
            setIsSearching(false)
            return
        }

        if (mode === SEARCH_MODES.identifier && trimmedValue.length < 3) {
            setUserInfo(null)
            setIsSearching(false)
            return
        }

        // Set searching trước debounce — UX phản hồi ngay, không chờ 700ms
        setIsSearching(true)
        debounceRef.current = setTimeout(() => {
            debounceRef.current = null
            const token = ++searchTokenRef.current
            dispatchLookup(trimmedValue, mode, idType, token)
        }, LOOKUP_DEBOUNCE)
    }, [dispatchLookup])

    const handleInputChange = useCallback((e) => {
        const value = e?.target?.value ?? ''
        setInputValue(value)
        setUserInfo(null)
        triggerLookup(value, searchModeRef.current, identifierTypeRef.current)
    }, [triggerLookup])

    const handleModeChange = useCallback((mode) => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        if (retryRef.current) clearTimeout(retryRef.current)
        setSearchMode(mode)
        searchModeRef.current = mode
        setInputValue('')
        setUserInfo(null)
        setIsSearching(false)
    }, [])

    const handleIdentifierTypeChange = useCallback((idType) => {
        setIdentifierType(idType)
        identifierTypeRef.current = idType
        setUserInfo(null)
        const current = inputValueRef.current
        if (current) {
            triggerLookup(current, searchModeRef.current, idType)
        }
    }, [triggerLookup])

    const suffix = useMemo(() => {
        if (isSearching) return <Spin size="small"/>
        if (!inputValue) return null
        if (userInfo?.name) return <HiCheckCircle size={16} className="text-green-500"/>
        if (userInfo !== null) return <HiXCircle size={16} className="text-orange-400"/>
        return null
    }, [isSearching, inputValue, userInfo])

    const handleChoose = useCallback(() => {
        handleApply({...userInfo})
        if (_.isFunction(toggle)) toggle(false)
    }, [handleApply, userInfo, toggle])

    return (
        <div className="flex flex-col gap-4">
            {/* Hướng dẫn - giải thích NGAY vì sao không có gợi ý-theo-tên (khác hẳn các lookup khác
                trong app như giáo xứ/tên thánh) - tránh user tưởng lỗi khi gõ tên không ra gì. */}
            <div className="p-3 flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-xl">
                <IoShieldCheckmarkOutline size={18} className="flex-shrink-0 mt-0.5 text-blue-500"/>
                <span className="text-xs text-blue-800 tracking-wider">{t('lookup account guidance')}</span>
            </div>

            {/* Tab mode */}
            <div className="flex gap-2">
                <ModeTab
                    active={searchMode === SEARCH_MODES.email}
                    icon={HiOutlineMail}
                    label={t('email')}
                    onClick={() => handleModeChange(SEARCH_MODES.email)}
                />
                <ModeTab
                    active={searchMode === SEARCH_MODES.identifier}
                    icon={HiOutlineIdentification}
                    label={t('identifier code')}
                    onClick={() => handleModeChange(SEARCH_MODES.identifier)}
                />
            </div>

            {/* Input */}
            <div className="flex flex-col gap-2">

                {searchMode === SEARCH_MODES.identifier && (
                    <ChipGroup
                        options={Object.values(IdentifierTypes)}
                        value={identifierType}
                        onChange={(e) => handleIdentifierTypeChange(e)}
                    />
                    // <Radio.Group
                    //     value={identifierType}
                    //     onChange={(e) => handleIdentifierTypeChange(e.target.value)}
                    //     className="custom-radio-button-sm"
                    //     optionType="button"
                    //     buttonStyle="solid">
                    //     {Object.values(IdentifierTypes).map((e, i) => (
                    //         <Radio.Button
                    //             key={i}
                    //             value={e.value}
                    //             style={{padding: '6px 8px', height: 32}}>
                    //             {t(e.label)}
                    //         </Radio.Button>
                    //     ))}
                    // </Radio.Group>
                )}
                <Input
                    size="large"
                    value={inputValue}
                    suffix={suffix}
                    autoFocus
                    onChange={handleInputChange}
                    placeholder={t(
                        searchMode === SEARCH_MODES.email
                            ? 'email placeholder'
                            : 'ex: id code'
                    )}
                />
            </div>

            {/* Kết quả */}
            <ResultCard
                t={t}
                userInfo={userInfo}
                isLoading={isSearching}
                searchMode={searchMode}
                onChoose={handleChoose}
            />
        </div>
    )
})

const UserLookupComponent = ({handleApply, children}) => {
    const t = useTranslate()

    return (
        <DrawDialog
            width={600}
            component={children}
            title={
                <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                    {t('account lookup')}
                </div>
            }>
            {(isToggle, toggle) => {
                // Guard: không render content khi drawer đóng
                if (!isToggle) return null

                return (
                    <UserLookupContent
                        handleApply={handleApply}
                        toggle={toggle}
                    />
                )
            }}
        </DrawDialog>
    )
}

export default UserLookupComponent
